import React from 'react';
import { useNavigate } from 'react-router-dom';
import StepCarousel from './StepCarousel';

export const leaseCreationSteps = [
  {
    id: 1,
    title: 'Landlord Details',
    description: 'Confirm who owns and manages the property, including contact information for notices and signatures.',
    screenshot: '/lease_form_screenshots/localhost_3000_create-lease_1lCYWFW25svTE87XOIXi(iPhone XR).png',
    features: ['Ownership & management info', 'Primary contact details', 'Digital signature preferences']
  },
  {
    id: 2,
    title: 'Property Profile',
    description: 'Document the unit address, type, and key facts so every agreement starts with accurate property data.',
    screenshot: '/lease_form_screenshots/Property_Details.png',
    features: ['Verified property address', 'Unit specifications', 'HOA & parking notes']
  },
  {
    id: 3,
    title: 'Tenant Roster',
    description: 'Add every resident and emergency contact to keep responsibilities documented.',
    screenshot: '/lease_form_screenshots/Tenant_Information.png',
    features: ['Primary tenant info', 'Additional occupants', 'Emergency contacts']
  },
  {
    id: 4,
    title: 'Lease Terms',
    description: 'Select the lease structure, timeline, and renewal rules that govern the tenancy.',
    screenshot: '/lease_form_screenshots/Lease_Terms.png',
    features: ['Fixed or month-to-month', 'Move-in & notice dates', 'Renewal and termination rules']
  },
  {
    id: 5,
    title: 'Rent & Finance',
    description: 'Set rent, deposits, and fee schedules with California considerations in mind.',
    screenshot: '/lease_form_screenshots/Financial_Terms.png',
    features: ['Monthly rent & proration', 'Security deposits', 'Late fees & grace periods']
  }
] as const;

const LeaseStepsSection: React.FC = () => {
  const navigate = useNavigate();
  const steps = leaseCreationSteps;

  return (
    <div style={{
      background: '#ffffff',
      padding: '80px 0',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px'
      }}>
        {/* Section Header */}
        <div className="steps-header" style={{
          textAlign: 'center',
          marginBottom: '60px'
        }}>
          <h2 className="steps-title" style={{
            fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: 700,
            color: '#0A0B0D',
            marginBottom: '24px',
            letterSpacing: '-0.02em'
          }}>
            Create your lease in
            <span style={{ color: '#0052FF' }}> 5 easy steps</span>
          </h2>
          <p className="steps-subtitle" style={{
            fontSize: 'clamp(1.2rem, 2.6vw, 1.4rem)',
            color: '#3A3F4C',
            maxWidth: '640px',
            margin: '0 auto',
            lineHeight: 1.7
          }}>
            Draft a California lease with these five core steps, then utilize the optional sections for the extras you want.
          </p>
        </div>

        {/* Carousel */}
        <StepCarousel
          steps={steps}
          badgeText="Step"
          featuresLabel="Features"
        />

        {/* CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '80px',
          padding: '60px 40px',
          background: 'linear-gradient(135deg, #F8F9FF 0%, #E7F3FF 100%)',
          borderRadius: '24px'
        }}>
          <h3 style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: '#0A0B0D',
            marginBottom: '16px'
          }}>
            Ready to create your first lease?
          </h3>
          <p style={{
            fontSize: '1.125rem',
            color: '#5B616E',
            marginBottom: '32px'
          }}>
            Join other California landlords who draft leases with guided, self-help templates.
          </p>
          <button
            type="button"
            style={{
              padding: '16px 32px',
              background: '#0052FF',
              color: 'white',
              border: 'none',
              borderRadius: '999px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => navigate('/signup')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#0041CC';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#0052FF';
            }}
          >
            Get started →
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .steps-header {
            margin-bottom: 60px !important;
            padding: 0 20px !important;
          }

          .steps-title {
            font-size: 2.5rem !important;
            margin-bottom: 20px !important;
          }

          .steps-subtitle {
            font-size: 1rem !important;
            padding: 0 10px !important;
          }
        }

        @media (max-width: 480px) {
          .steps-header {
            margin-bottom: 40px !important;
            padding: 0 16px !important;
          }

          .steps-title {
            font-size: 2rem !important;
            margin-bottom: 16px !important;
          }

          .steps-subtitle {
            font-size: 1rem !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LeaseStepsSection;
